import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { selectRmRealtimeOn, setRmLoading } from 'state/routeMonitoringSlice';
import {
  SavedRouteAndFeature,
  selectCurrentSavedRoutes,
  setCurrentSavedRoutes,
  setStatsSavedRoutes,
  setTempStatsSavedRoutes,
  useGetRouteMonitoringNowQuery,
  useGetRouteMonitoringQuery,
  useGetSavedRoutesWithGeojsonQuery,
  useGetSegmentsMonitoringNowQuery,
  useGetSegmentsMonitoringQuery,
} from 'state/savedRouteSlices';
import {
  selectComparison,
  selectProject,
  selectRmEndDateTime,
  selectRmStartDateTime,
  selectRouteSelection,
  selectSlowdownThreshold,
} from 'state/workflowSlice';

import { isEndAfterStart } from 'features/common/datetimeUtils';
import {
  routeSelectionIsSavedRoute,
  useRouteInfo,
} from 'features/common/hooks/useRouteInfo';
import { deepEquals } from 'features/common/utils';
import { useAutoSetLayerFromTargetDate } from 'features/workflow_nrt/WorkflowRecent';

function useMonitoringSelectors() {
  const project_slug = useAppSelector(selectProject);
  const comparison = useAppSelector(selectComparison);
  const threshold = useAppSelector(selectSlowdownThreshold);
  const realtimeOn = useAppSelector(selectRmRealtimeOn);
  const routes = useAppSelector(selectCurrentSavedRoutes);

  return {
    project_slug,
    comparison,
    threshold,
    realtimeOn,
    routes,
  };
}

function useRefetchOnRouteChange(
  routes: SavedRouteAndFeature[],
  refetch: () => void,
  isUninitialized: boolean,
) {
  // Slightly overcomplicated but the FE updates the routes list
  // then dispatches an update from the BE which rewrites the routes list
  // to the same state as the FE did, causing 2x refetch

  const [lastQueriedRoutes, setLastQueriedRoutes] = useState([...routes]);
  useEffect(() => {
    if (!isUninitialized && !deepEquals(lastQueriedRoutes, routes)) {
      refetch();
      setLastQueriedRoutes([...routes]);
    }
  }, [routes]);
}

function useHandleResultEffect(result: any, oldDataAccepted: boolean = false) {
  const dispatch = useAppDispatch();
  const { isLoading, isFetching, isError, isSuccess, data } = result;

  useEffect(() => {
    if ((isLoading || isFetching) && (!oldDataAccepted || !data)) {
      dispatch(setRmLoading(true));
    } else if (isError) {
      dispatch(setStatsSavedRoutes(undefined));
      dispatch(setRmLoading(false));
    } else if (isSuccess && data) {
      dispatch(setStatsSavedRoutes(data));
      dispatch(setRmLoading(false));
    }
  }, [result, dispatch]);
}

export function useRMDataProvider() {
  const { project_slug, comparison, threshold, realtimeOn, routes } =
    useMonitoringSelectors();
  const start_date: string = useAppSelector(selectRmStartDateTime);
  const end_date: string = useAppSelector(selectRmEndDateTime);

  const skip =
    !project_slug ||
    !start_date ||
    !end_date ||
    !isEndAfterStart(start_date, end_date) ||
    comparison == null ||
    threshold == null ||
    realtimeOn;

  const result = useGetRouteMonitoringQuery(
    { project_slug, comparison, threshold, start_date, end_date },
    { skip },
  );
  useRefetchOnRouteChange(routes, result.refetch, result.isUninitialized);

  useHandleResultEffect(result);

  return result;
}

export function useRealtimeRMDataProvider() {
  const { project_slug, comparison, threshold, realtimeOn, routes } =
    useMonitoringSelectors();

  const skip =
    !project_slug || comparison == null || threshold == null || !realtimeOn;

  const result = useGetRouteMonitoringNowQuery(
    { project_slug, comparison, threshold },
    {
      skip,
      pollingInterval: 1000 * 60,
    },
  );
  useRefetchOnRouteChange(routes, result.refetch, result.isUninitialized);

  useHandleResultEffect(result, true);

  return result;
}

/** temp route */

function useHandleTempResultEffect(
  result: any,
  oldDataAccepted: boolean = false,
) {
  const dispatch = useAppDispatch();
  const { isLoading, isFetching, isError, isSuccess, data } = result;
  useEffect(() => {
    if ((isLoading || isFetching) && (!oldDataAccepted || !data)) {
      dispatch(setTempStatsSavedRoutes(undefined));
    } else if (isError) {
      dispatch(setTempStatsSavedRoutes(undefined));
    } else if (isSuccess && data) {
      dispatch(setTempStatsSavedRoutes(data));
    }
  }, [result, dispatch]);
}

export function useRMSegmentDataProvider() {
  const { project_slug, comparison, threshold, realtimeOn } =
    useMonitoringSelectors();
  const routeSelection = useAppSelector(selectRouteSelection);
  const start_date: string = useAppSelector(selectRmStartDateTime);
  const end_date: string = useAppSelector(selectRmEndDateTime);
  const { basicRouteInfo } = useRouteInfo();

  const skip =
    (routeSelection && routeSelectionIsSavedRoute(routeSelection)) ||
    !project_slug ||
    !start_date ||
    !end_date ||
    !isEndAfterStart(start_date, end_date) ||
    comparison == null ||
    threshold == null ||
    realtimeOn ||
    !basicRouteInfo.realizedSegments;

  const result = useGetSegmentsMonitoringQuery(
    {
      project_slug,
      comparison,
      threshold,
      start_date,
      end_date,
      segmentIds: basicRouteInfo.realizedSegments,
    },
    {
      skip,
    },
  );
  useHandleTempResultEffect(result, false);

  return result;
}

export function useSegmentRealtimeRMDataProvider() {
  const { project_slug, comparison, threshold, realtimeOn } =
    useMonitoringSelectors();
  const routeSelection = useAppSelector(selectRouteSelection);
  const { basicRouteInfo } = useRouteInfo();
  const skip =
    (routeSelection && routeSelectionIsSavedRoute(routeSelection)) ||
    !project_slug ||
    comparison == null ||
    threshold == null ||
    !realtimeOn ||
    !basicRouteInfo.realizedSegments;

  const result = useGetSegmentsMonitoringNowQuery(
    {
      project_slug,
      comparison,
      threshold,
      segmentIds: basicRouteInfo.realizedSegments,
    },
    {
      skip,
      pollingInterval: 1000 * 60,
    },
  );

  useHandleTempResultEffect(result, false);

  return result;
}

export function useCurrentRoutesWithGeoJson() {
  const project = useAppSelector(selectProject);
  const memoizedLayer = useAutoSetLayerFromTargetDate();
  const dispatch = useAppDispatch();

  const { currentData } = useGetSavedRoutesWithGeojsonQuery(
    { project_slug: project, layerId: memoizedLayer.layer },
    {
      skip: !project || !memoizedLayer.layer,
    },
  );

  const [isReady, setIsReady] = useState(!!currentData);

  useEffect(() => {
    if (currentData) {
      dispatch(setCurrentSavedRoutes(currentData));
      setIsReady(true);
    }
  }, [currentData]);

  useEffect(() => {
    if (!currentData) {
      setIsReady(false);
    }
  }, [project]);

  return { isReady, currentData };
}
